<template>
    <common-content-wrapper width="tiny">
        <common-content-spacer height="medium">
            <div class="ref-heading-line">
                <div class="ref-status-wrapper">
                    <referrals-status :referral="newReferral" :isInline="true"></referrals-status>
                </div>
                <div v-if="!(isConfirmed || isJustConfirmed || isDeclined)" class="ref-context-menu-wrapper">
                    <referral-details-context-menu
                        :referral="newReferral"
                        :isDisabled="isReferralCtxMenuDisabled || isToUserSubscriptionNetwork"
                        @emitEvent="setContextAction"
                    ></referral-details-context-menu>
                </div>
            </div>

            <common-content-wrapper v-if="isViewDetailsAction" width="medium">
                <common-content-spacer height="large">
                    <referral-details class="referral-view-details__container" :referral="referral"></referral-details>

                    <common-buttons>
                        <common-button
                            :label="$t('pages.referrals.show.view_details.buttons.back')"
                            type="primary"
                            @click="closeReferralDetails"
                        ></common-button>
                    </common-buttons>
                </common-content-spacer>
            </common-content-wrapper>

            <pending-confirmed v-else-if="isConfirmed || isJustConfirmed" :referral="newReferral"></pending-confirmed>

            <pending-declined v-else-if="isDeclined" :referral="referral" :type="declineInfoType"></pending-declined>

            <pending-declined
                v-else-if="referral.isInsuranceJustDeclined"
                :referral="referral"
                type="cancelled"
            ></pending-declined>

            <div v-else>
                <pending-details
                    :referral="referral"
                    :requestedAction="requestedAction"
                    :isToUserSubscriptionNetwork="isToUserSubscriptionNetwork"
                    @confirm="handleConfirm($event)"
                    @decline="handleDecline($event)"
                    @reschedule="handleReschedule($event)"
                    @match="handleMatch()"
                    @cancel="handleCancel()"
                    @referral-changed="emitEvent({ name: 'referral-changed', payload: $event })"
                ></pending-details>

                <common-content-spacer v-if="isReferringPhysician && isReferralError(newReferral)" height="medium">
                    <eligible-physicians v-if="showEligiblePhysicians" :referral="newReferral"></eligible-physicians>
                </common-content-spacer>
            </div>
        </common-content-spacer>

        <disclaimer-modal
            :isOpen="isDisclaimerModalOpen"
            @close="isDisclaimerModalOpen = false"
            @continue="confirmReferral($event)"
        ></disclaimer-modal>

        <common-modal
            v-if="isPrescriptionModalOpen"
            :isOpen="isPrescriptionModalOpen"
            width="large"
            @close="closePrescriptionModal"
        >
            <referral-prescription :referral="referral"></referral-prescription>
        </common-modal>
    </common-content-wrapper>
</template>

<script>
    import ReferralsStatus from 'components/page-elements/ReferralsStatus'
    import PendingDetails from 'components/page-elements/referrals-show-pending/PendingDetails'
    import PendingConfirmed from 'components/page-elements/referrals-show-pending/PendingConfirmed'
    import PendingDeclined from 'components/page-elements/referrals-show-pending/PendingDeclined'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralRescheduleMixin from 'mixins/ReferralRescheduleMixin'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralDetailsContextMenu from 'components/page-elements/ReferralDetailsContextMenu'
    import ReferralDetails from 'components/page-elements/referrals/ReferralDetails'
    import ReferralPrescription from 'components/page-elements/referrals/ReferralPrescription'
    import DisclaimerModal from 'components/page-elements/referrals/DisclaimerModal'
    import EligiblePhysicians from 'components/page-elements/eligible-physicians/EligiblePhysicians'
    import ReferralMatchMixin from 'mixins/ReferralMatchMixin'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import dayjs from 'dayjs'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import DemoReferral from 'services/DemoReferral'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'

    export default {
        mixins: [ReferralRescheduleMixin, ReferralStatusMixin, ReferralMatchMixin],

        components: {
            ReferralsStatus,
            PendingDetails,
            PendingConfirmed,
            PendingDeclined,
            ReferralDetailsContextMenu,
            DisclaimerModal,
            EligiblePhysicians,
            ReferralDetails,
            ReferralPrescription,
        },

        props: {
            referral: { type: Object, required: true },
        },

        data() {
            return {
                isConfirmed: false,
                isDeclined: false,
                declineInfoType: 'declined',
                referralNewObj: undefined,
                contextMenuAction: undefined,
                requestedActionTimestamp: dayjs(),
                isDisclaimerModalOpen: false,
                isPrescriptionModalOpen: false,
            }
        },

        computed: {
            isToUserSubscriptionNetwork() {
                return this.$auth.getPractice().subscription === PracticePlanEnum.NETWORK
            },
            newReferral() {
                if (this.referralNewObj) return this.referralNewObj
                else return this.referral
            },
            showEligiblePhysicians() {
                return this.referral?.practice?.practiceType !== PracticeTypeEnum.ANCILLARY
            },
            isJustConfirmed() {
                return this.referral && this.referral.status === ReferralStatusEnum.PATIENT_VISIT_PENDING
            },

            requestedAction() {
                return {
                    action: this.$route.query.action || this.contextMenuAction || '',
                    timestamp: this.requestedActionTimestamp,
                }
            },

            isReferringPhysician() {
                return this.$route.params.type === 'made'
            },

            isReferralCtxMenuDisabled() {
                if (this.isViewDetailsAction) {
                    return true
                }

                if (this.isReferringPhysician) {
                    return (
                        this.isReferralCompleted(this.latestReferral) ||
                        (this.isReferralWaitingForPatient(this.latestReferral) && this.isAppointmentInPast(this.referral))
                    )
                } else {
                    return this.isReferralError(this.latestReferral)
                }
            },

            isViewDetailsAction() {
                return this.contextMenuAction === 'viewDetails'
            },

            isDownloadPdfAction() {
                return this.contextMenuAction === 'downloadPdf'
            },
        },

        methods: {
            setContextAction(event) {
                this.contextMenuAction = event.name
                this.requestedActionTimestamp = dayjs()

                // Open prescription modal for downloadPdf action
                if (event.name === 'downloadPdf') {
                    this.isPrescriptionModalOpen = true
                }
            },

            closePrescriptionModal() {
                this.isPrescriptionModalOpen = false
                this.contextMenuAction = null
            },

            closeReferralDetails() {
                this.contextMenuAction = null
            },

            handleMatch() {
                this.goToMatchSearchPage(
                    this.newReferral,
                    this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id,
                )
            },

            handleCancel() {
                return ReferralsAPI.cancel(
                    this.$auth.isUserPracticeOther(),
                    this.referral.id,
                    this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id,
                ).then((res) => {
                    this.emitReferralData(res)
                    this.referralNewObj = res
                    this.declineInfoType = 'cancelled'
                    this.isDeclined = true
                })
            },

            handleConfirm() {
                if (this.referral.shouldShowPatientInfo) {
                    return this.confirmReferral()
                }
                this.isDisclaimerModalOpen = true
            },

            confirmReferral() {
                this.isDisclaimerModalOpen = false
                if (this.$auth.isUserPracticeDemo()) {
                    localStorage.setItem('disclaimerAceppted', true)
                    this.emitReferralData(DemoReferral.getConfirmedReferral())
                    return false
                }
                return ReferralsAPI.accept(this.referral.id).then((res) => {
                    this.emitReferralData(res)
                    this.referralNewObj = res
                    this.isConfirmed = true
                })
            },

            handleDecline({ reason, message }) {
                let closeReason = ''
                let comment = undefined

                switch (reason) {
                    case 'slot_not_available':
                        closeReason = reason.toUpperCase()
                        break
                    case 'other':
                        closeReason = 'OTHER'
                        comment = message
                        break
                    default:
                        closeReason = 'OTHER'
                }

                return ReferralsAPI.decline(
                    this.referral.id,
                    this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id,
                    closeReason,
                    comment,
                ).then((res) => {
                    this.emitReferralData(res)
                    this.referralNewObj = res
                    this.declineInfoType = 'declined'
                    this.isDeclined = true
                })
            },

            handleReschedule() {
                return this.reschedule(this.referral)
            },

            emitReferralData(referral) {
                this.emitEvent({
                    name: 'referral-changed',
                    payload: {
                        patient: referral.patient,
                        note: referral.note,
                        shouldShowPatientInfo: referral.shouldShowPatientInfo,
                    },
                })
            },
        },
    }
</script>
